import { useCallback, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { Alert, Checkbox, FormControlLabel, TextField } from '@mui/material';
import * as yup from 'yup';

import { Button } from '../../../components/button/Button';
import IFormError from '../../../interfaces/IFormError';
import { useValidateSchema } from '../../../services/useValidateSchema';
import { useHubSpotService } from '../../../services/useHubSpotService';
import { useAuth } from '../../../hooks/auth';

interface IProps {
  orderId: string;
  orderTypeId: number;
  open: boolean;
  deny: () => void;
  cloneOrder: (id: string, keepItems: boolean, hubSpotId: string, newCustomerId: string) => void;
}

interface IFormErrorResult {
  errors: IFormError | null;
  isValid: boolean;
}

const OrderCloneDialog = ({ orderId, orderTypeId, open, cloneOrder, deny }: IProps) => {
  const validation = useValidateSchema();
  const { validateHubSpotDealId } = useHubSpotService();
  const { orderParams, state } = useAuth();

  const [keepItems, setKeepItems] = useState(true);
  const [hubSpotId, setHubSpotId] = useState('');
  const [errorsHubSpot, setErrorsHubSpot] = useState<IFormError | null>();
  const [hubSpotDealIdIsValid, setHubSpotDealIdIsValid] = useState(false);
  const [hubSpotMessageError, setHubSpotMessageError] = useState('');
  const [loading, setLoading] = useState(false);
  const [newCustomerId, setNewCustomerId] = useState('');

  const handleCloseDialog = () => {
    setKeepItems(true);
    setHubSpotId('');
    setErrorsHubSpot({});
    setHubSpotDealIdIsValid(false);
    setHubSpotMessageError('');
    deny();
  };

  const handleCloneOrder = (orderId: string, keepItems: boolean, hubSpotId: string, newCustomerId: string) => {
    cloneOrder(orderId, keepItems, hubSpotId, newCustomerId);
  };

  const handleValidateHubSpotDealId = useCallback(async () => {
    let resultHubSpot: IFormErrorResult | null = null;
    setErrorsHubSpot(null);

    const data = { hubSpotId, orderTypeId };

    const schemaHubSpot = yup.object({
      hubSpotId: yup.string().required('Campo obrigatório').min(5, 'Campo obrigatório.'),
    });

    resultHubSpot = await validation.validate(schemaHubSpot, data || {});

    if (!resultHubSpot.isValid) {
      setErrorsHubSpot(resultHubSpot.errors);
      return;
    }

    setLoading(true);
    await validateHubSpotDealId(hubSpotId, orderTypeId || 1, state.seller?.id || '')
      .then(response => {
        if (!response.customer) {
          setHubSpotMessageError('Pedido não localizado no HubSpot, contate o suporte.');
          setLoading(false);
          return;
        }

        setNewCustomerId(response.customer?.id || '');

        setHubSpotDealIdIsValid(true);
      })
      .catch(error => {
        setHubSpotMessageError(error.message || 'Erro ao consultar pedido no HubSpot, contate o suporte.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [hubSpotId, orderTypeId]);

  return (
    <Dialog open={open}>
      <DialogContent sx={{ width: '250px' }}>
        {hubSpotDealIdIsValid || !orderParams?.integrateHubSpot ? (
          <>
            <DialogContentText style={{ marginBottom: '8px' }}>
              <strong style={{ fontSize: '16px' }}>Deseja realmente duplicar o pedido?</strong>
            </DialogContentText>

            <FormControlLabel
              control={
                <Checkbox
                  checked={keepItems}
                  onClick={() => {
                    setKeepItems(!keepItems);
                  }}
                />
              }
              label="Manter os itens"
            />

            <DialogActions style={{ justifyContent: 'center', marginTop: '16px' }}>
              <Button
                onClick={() => {
                  setHubSpotDealIdIsValid(false);
                }}
                color="error"
                variant="contained"
              >
                Voltar
              </Button>
              <Button
                onClick={() => handleCloneOrder(orderId, keepItems, hubSpotId, newCustomerId)}
                autoFocus
                variant="contained"
              >
                Duplicar
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContentText style={{ marginBottom: '8px' }}>
              <strong style={{ fontSize: '16px' }}>Informe o ID da Negociação no HubSpot</strong>
            </DialogContentText>

            <TextField
              sx={{ width: '100%', marginTop: '16px', marginBottom: '12px' }}
              size="small"
              label="ID Negociação HubSpot"
              value={hubSpotId}
              onChange={e => setHubSpotId(e.target.value)}
              autoComplete="off"
              error={Boolean(errorsHubSpot?.hubSpotId)}
              helperText={errorsHubSpot?.hubSpotId}
            />

            {hubSpotMessageError && <Alert severity="error">{hubSpotMessageError}</Alert>}

            <DialogActions style={{ justifyContent: 'center', marginTop: '16px' }}>
              <Button onClick={handleCloseDialog} color="error" variant="contained" disabled={loading}>
                Cancelar
              </Button>
              <Button
                onClick={handleValidateHubSpotDealId}
                autoFocus
                variant="contained"
                disabled={loading}
                loading={loading}
              >
                Validar
              </Button>
            </DialogActions>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrderCloneDialog;
